// The Goblic run-tool catalog — see
// docs/plans/2026-09-30-runners/04-run-tools.md §2.4.
//
// One definition per tool (name, a one-sentence description, and its Zod
// input), used by:
// - the API to render the tool section of the work instructions (01) and to
//   filter `goblicTools` into the run payload from a role's `runSettings`
//   (05, `RoleRunSettingsSchema.web`/`.delegate`);
// - the worker to validate incoming `tool.call` frames and write
//   `/tmp/.goblic-run/tools.json` for the sandbox;
// - `goblic-tools` (the MCP server in the sandbox, 04 §2.4) and the Pi
//   extension (03 §2.4) to register exactly these tools with the runner.
//
// This file only defines the contract. The handlers that actually execute
// each tool are worker-side (`run-tools/handlers.ts`, owned by 04/05/06) and
// are deliberately not part of this package (server-only).

import { z } from 'zod'
import { zodToJsonSchema } from 'zod-to-json-schema'
import { MAX_SUBTASKS_PER_CALL } from '../types/subtasks.js'
import { SubtaskDraftInputSchema } from '../validators/subtasks.js'
import { KB_FOR_PATHS_MAX_FILES, KB_SEARCH_MAX_LIMIT } from '../kb/limits.js'
import { REPORT_MAX_HTML_BYTES, REPORT_MESSAGE_MAX_LENGTH, REPORT_SUMMARY_MAX_LENGTH, REPORT_TITLE_MAX_LENGTH } from '../reports/limits.js'

// ---------------------------------------------------------------------------
// The RunToolDef contract
// ---------------------------------------------------------------------------

export interface RunToolDef<TInput extends z.ZodTypeAny = z.ZodTypeAny> {
  readonly name: string
  /** One sentence, at most 160 characters — this is what goes in the work instructions' tool list (01 §2.2). */
  readonly description: string
  readonly input: TInput
  /** 'default': every role gets it, no switch exists. Otherwise gated by the named `runSettings` switch — see `isRunToolAllowedBySettings`. */
  readonly availability: 'default' | { readonly setting: RunToolSetting }
  /** Whether a sub-task (`delegate`, 06) may call this tool too. */
  readonly subtask: boolean
  /** Whether the worker may keep the call open (up to 15 minutes) awaiting a human decision (04 §2.6). */
  readonly waits: boolean
  /** Which deelplan owns this tool's worker-side handler. */
  readonly owner: '04' | '05' | '06' | 'kb' | 'reports' | 'subtasks'
}

/**
 * The `runSettings` switch a gated tool hangs off. `'web'`/`'delegate'` mean
 * `runSettings.web.enabled`/`runSettings.delegate.enabled`; `'kb'` means
 * `runSettings.kb.enabled` (`kb_write` also needs `runSettings.kb.write`, see
 * `KB_WRITE_TOOLS`); `'reports'` means `runSettings.reports.read` for
 * `report_read` and `.write` for `report_write`/`report_attach`
 * (`REPORT_WRITE_TOOLS`; docs/plans/2026-10-02-rapporten-ontwerp.md §2.7);
 * the other four mean the like-named `runSettings.tools.*` switch (per-role
 * toggles for `ask_human`/`request_approval`/`post_comment`/
 * `create_followup`, each on by default).
 */
export type RunToolSetting =
  | 'web'
  | 'delegate'
  | 'kb'
  | 'reports'
  | 'askHuman'
  | 'requestApproval'
  | 'postComment'
  | 'createFollowup'
  | 'createSubtasks'

/** Identity helper that keeps each tool's concrete `input` type instead of widening it to `ZodTypeAny`. */
function defineRunTool<TInput extends z.ZodTypeAny>(def: RunToolDef<TInput>): RunToolDef<TInput> {
  return def
}

export const MAX_RUN_TOOL_DESCRIPTION_LENGTH = 160

// ---------------------------------------------------------------------------
// get_task
// ---------------------------------------------------------------------------

export const GetTaskInputSchema = z.object({})

export const GetTaskTool = defineRunTool({
  name: 'get_task',
  description: "Get the task's title, instructions, acceptance criteria, workflow variables and recent context.",
  input: GetTaskInputSchema,
  availability: 'default',
  subtask: false,
  waits: false,
  owner: '04',
})

// ---------------------------------------------------------------------------
// report_progress
// ---------------------------------------------------------------------------

/**
 * `done`/`next`/`decisions` (checkpoints contract §3.2) feed the run's
 * worklog (`RunWorklogSchema`, `mergeWorklog`): a list that is sent replaces
 * the previous one, an omitted list keeps it. The worklog is what another
 * agent gets when it takes the work over.
 */
export const ReportProgressInputSchema = z.object({
  message: z.string().min(1),
  percent: z.number().min(0).max(100).optional(),
  /** What is finished so far (whole list, not just since the last call). */
  done: z.array(z.string().min(1).max(500)).max(20).optional(),
  /** What comes next, in order. */
  next: z.array(z.string().min(1).max(500)).max(20).optional(),
  /** Choices made that a successor should not silently undo. */
  decisions: z.array(z.string().min(1).max(500)).max(20).optional(),
})

export const ReportProgressTool = defineRunTool({
  name: 'report_progress',
  description:
    'Report a short progress update at a real milestone, not every step. Include done, next and decisions so another agent can take over the work if this run stops.',
  input: ReportProgressInputSchema,
  availability: 'default',
  subtask: false,
  waits: false,
  owner: '04',
})

// ---------------------------------------------------------------------------
// finish
// ---------------------------------------------------------------------------

export const FinishStatusSchema = z.enum(['completed', 'blocked'])

/**
 * What the agent claims it ran (01 §2.2: "the exact command and its
 * outcome"). The authoritative changed-files/test counts still come from
 * the worker's own `ResultCollector`, not from this self-report (04 §2.5).
 */
export const FinishTestReportSchema = z.object({
  command: z.string().min(1),
  passed: z.boolean(),
})

export const FinishInputSchema = z.object({
  status: FinishStatusSchema,
  summary: z.string().min(1),
  pr_body: z.string().min(1).optional(),
  open_points: z.array(z.string().min(1)),
  tests: z.array(FinishTestReportSchema).max(20).optional(),
  next_state: z.string().min(1).optional(),
})

export const FinishTool = defineRunTool({
  name: 'finish',
  description: "Report the run's final outcome once, then stop — the worker blocks further tools after this.",
  input: FinishInputSchema,
  availability: 'default',
  subtask: false,
  waits: false,
  owner: '04',
})

// ---------------------------------------------------------------------------
// ask_human
// ---------------------------------------------------------------------------

export const AskHumanKindSchema = z.enum(['text', 'yes_no', 'single_choice', 'multiple_choice'])
export type AskHumanKind = z.infer<typeof AskHumanKindSchema>

const CHOICE_KINDS: readonly AskHumanKind[] = ['single_choice', 'multiple_choice']

/** Shared by the single-question fields and each item of `questions` — same 'options'/'allowOther' rules either way. */
function checkAskHumanShape(
  input: { kind: AskHumanKind; options?: string[]; allowOther?: boolean },
  ctx: z.RefinementCtx,
  path: (string | number)[] = [],
): void {
  const isChoice = CHOICE_KINDS.includes(input.kind)
  if (isChoice && !input.options) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, 'options'], message: `options is required when kind is '${input.kind}'` })
  }
  if (!isChoice && input.options) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, 'options'], message: "options is only valid for 'single_choice'/'multiple_choice'" })
  }
  if (!isChoice && input.allowOther) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, 'allowOther'], message: "allowOther is only valid for 'single_choice'/'multiple_choice'" })
  }
}

/** One sub-question of a multi-question `ask_human` call — same shape and validation as the top-level single-question fields. */
export const AskHumanQuestionItemSchema = z
  .object({
    /** Lets the model refer back to this sub-question later; otherwise identified by its position. */
    id: z.string().min(1).optional(),
    question: z.string().min(1),
    kind: AskHumanKindSchema.default('text'),
    options: z.array(z.string().min(1)).min(2).max(8).optional(),
    allowOther: z.boolean().optional(),
  })
  .superRefine((input, ctx) => checkAskHumanShape(input, ctx))

export type AskHumanQuestionItem = z.infer<typeof AskHumanQuestionItemSchema>

export const AskHumanInputSchema = z
  .object({
    /** Required when `questions` is omitted (classic single-question call); otherwise optional and, if given, acts as an intro/title for the whole form. */
    question: z.string().min(1).optional(),
    context: z.string().min(1).optional(),
    /** How the UI should ask: a free-text box ('text', the default), two buttons ('yes_no'), a radio list, or checkboxes. Only meaningful when `questions` is omitted. */
    kind: AskHumanKindSchema.default('text'),
    /** 2-8 short labels; required for 'single_choice'/'multiple_choice', refused for every other kind. Only meaningful when `questions` is omitted. */
    options: z.array(z.string().min(1)).min(2).max(8).optional(),
    /** 'single_choice'/'multiple_choice' only: also show a free-text "other" field. Only meaningful when `questions` is omitted. */
    allowOther: z.boolean().optional(),
    /** 1-8 sub-questions asked in one call and shown as one form; put every open point here instead of calling ask_human repeatedly. */
    questions: z.array(AskHumanQuestionItemSchema).min(1).max(8).optional(),
  })
  .superRefine((input, ctx) => {
    if (input.questions && input.questions.length > 0) {
      if (input.options) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['options'], message: "options is only valid per-item inside 'questions'" })
      }
      if (input.allowOther) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['allowOther'], message: "allowOther is only valid per-item inside 'questions'" })
      }
      return
    }
    if (!input.question) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['question'], message: "question is required when 'questions' is not given" })
      return
    }
    checkAskHumanShape(input, ctx)
  })

export type AskHumanInput = z.infer<typeof AskHumanInputSchema>

/**
 * Normalizes an already-validated `AskHumanInputSchema` result into its
 * sub-question list: `questions` as-is, or the single-question fields
 * wrapped as a one-item list — so worker/API code only ever needs to
 * handle one shape (plan backward-compat: a single question is a
 * one-item list internally).
 */
export function askHumanQuestions(input: AskHumanInput): AskHumanQuestionItem[] {
  if (input.questions && input.questions.length > 0) return input.questions
  return [{ question: input.question!, kind: input.kind, options: input.options, allowOther: input.allowOther }]
}

export const AskHumanTool = defineRunTool({
  name: 'ask_human',
  description:
    'Ask a human and wait (up to 15 min) for an answer. Put all open points in one call via `questions`; use yes_no/single_choice/multiple_choice for a known set.',
  input: AskHumanInputSchema,
  availability: { setting: 'askHuman' },
  subtask: false,
  waits: true,
  owner: '04',
})

// ---------------------------------------------------------------------------
// request_approval
// ---------------------------------------------------------------------------

export const ApprovalRiskSchema = z.enum(['low', 'medium', 'high', 'critical'])

export const RequestApprovalInputSchema = z.object({
  action: z.string().min(1),
  reason: z.string().min(1),
  risk: ApprovalRiskSchema,
})

export const RequestApprovalTool = defineRunTool({
  name: 'request_approval',
  description: 'Ask a human to approve a risky or privileged action before taking it.',
  input: RequestApprovalInputSchema,
  availability: { setting: 'requestApproval' },
  subtask: false,
  waits: true,
  owner: '04',
})

// ---------------------------------------------------------------------------
// post_comment
// ---------------------------------------------------------------------------

export const PostCommentInputSchema = z.object({
  body: z.string().min(1).max(4000),
})

export const PostCommentTool = defineRunTool({
  name: 'post_comment',
  description: 'Post a short markdown comment on the task, visible to humans tracking it (max 5 per run).',
  input: PostCommentInputSchema,
  availability: { setting: 'postComment' },
  subtask: false,
  waits: false,
  owner: '04',
})

// ---------------------------------------------------------------------------
// create_followup
// ---------------------------------------------------------------------------

export const CreateFollowupInputSchema = z.object({
  title: z.string().min(1),
  instructions: z.string().min(1),
  reason: z.string().min(1),
})

export const CreateFollowupTool = defineRunTool({
  name: 'create_followup',
  description: "Create a follow-up task for work that's out of scope for this run (max 3 per run).",
  input: CreateFollowupInputSchema,
  availability: { setting: 'createFollowup' },
  subtask: false,
  waits: false,
  owner: '04',
})

// ---------------------------------------------------------------------------
// create_subtasks — gated by runSettings.tools.createSubtasks (off by
// default). docs/plans/2026-10-03-subtaken.md §5. API side:
// POST /internal/runs/:runId/subtasks.
// ---------------------------------------------------------------------------

export const CreateSubtasksInputSchema = z.object({
  subtasks: z.array(SubtaskDraftInputSchema).min(1).max(MAX_SUBTASKS_PER_CALL),
  /** Remove every not-yet-started subtask of this task first (re-planning). */
  replace: z.boolean().optional(),
})

export const CreateSubtasksTool = defineRunTool({
  name: 'create_subtasks',
  description:
    'Split this task into subtasks (drafts, max 20 per call, 50 in total), each its own task and PR; dependsOn makes one wait for others.',
  input: CreateSubtasksInputSchema,
  availability: { setting: 'createSubtasks' },
  subtask: false,
  waits: false,
  owner: 'subtasks',
})

// ---------------------------------------------------------------------------
// web_search — gated by runSettings.web.enabled; adapter details are 05's
// (search/{searxng,brave,tavily,exa}.ts). No call signature is fixed by 04,
// so this is the provisional contract: a query and an optional result count.
// ---------------------------------------------------------------------------

export const WebSearchInputSchema = z.object({
  query: z.string().min(1),
  count: z.number().int().min(1).max(10).optional(),
})

export const WebSearchTool = defineRunTool({
  name: 'web_search',
  description: "Search the web through the role's configured search connection.",
  input: WebSearchInputSchema,
  availability: { setting: 'web' },
  subtask: true,
  waits: false,
  owner: '05',
})

// ---------------------------------------------------------------------------
// web_fetch — gated by runSettings.web.enabled; signature per 05 §2.3.
// ---------------------------------------------------------------------------

export const WebFetchInputSchema = z.object({
  url: z.string().url(),
  /** Continue reading a previously-truncated fetch from the worker's per-run cache. */
  startIndex: z.number().int().min(0).optional(),
})

export const WebFetchTool = defineRunTool({
  name: 'web_fetch',
  description: 'Fetch a URL and return its content as truncated, plain-text-marked-untrusted content.',
  input: WebFetchInputSchema,
  availability: { setting: 'web' },
  subtask: true,
  waits: false,
  owner: '05',
})

// ---------------------------------------------------------------------------
// delegate — gated by runSettings.delegate.enabled; signature per 06 §2.1.
// ---------------------------------------------------------------------------

export const DelegateTaskInputSchema = z
  .object({
    title: z.string().min(1).max(80),
    instruction: z.string().min(1).max(8000),
    mode: z.enum(['read', 'write']).default('read'),
    writePaths: z.array(z.string().min(1)).min(1).max(20).optional(),
    model: z.string().min(1).optional(),
  })
  .superRefine((task, ctx) => {
    if (task.mode === 'write' && (!task.writePaths || task.writePaths.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['writePaths'],
        message: "writePaths is required when mode is 'write'",
      })
    }
  })

export const DelegateInputSchema = z.object({
  tasks: z.array(DelegateTaskInputSchema).min(1).max(3),
  timeoutMinutes: z.number().int().min(1).max(15).optional(),
})

export const DelegateTool = defineRunTool({
  name: 'delegate',
  description: "Run up to 3 short, focused sub-tasks, using the run's own budget and guard.",
  input: DelegateInputSchema,
  availability: { setting: 'delegate' },
  // A sub-task cannot itself delegate — one level deep is fixed (06 §2.3).
  subtask: false,
  waits: false,
  owner: '06',
})

// ---------------------------------------------------------------------------
// Knowledge base — gated by runSettings.kb (docs/plans/2026-10-02-
// kennisbank-ontwerp.md §7.1). Paths start with `project/` (the run's own
// project) or `workspace/`. API side: /internal/runs/:runId/kb/*.
// ---------------------------------------------------------------------------

export const KbListInputSchema = z.object({
  /** e.g. `project/areas`; omitted = both spaces from the root. */
  folder: z.string().max(200).optional(),
})

export const KbListTool = defineRunTool({
  name: 'kb_list',
  description: 'List knowledge base pages (path, title, summary) in a folder such as project/areas; max 200 entries.',
  input: KbListInputSchema,
  availability: { setting: 'kb' },
  subtask: true,
  waits: false,
  owner: 'kb',
})

export const KbReadInputSchema = z.object({
  /** e.g. `project/areas/api-routes.md`. */
  path: z.string().min(1).max(220),
})

export const KbReadTool = defineRunTool({
  name: 'kb_read',
  description: 'Read a knowledge base page (frontmatter and Markdown) with its rev and a stale warning when the code may have moved on.',
  input: KbReadInputSchema,
  availability: { setting: 'kb' },
  subtask: true,
  waits: false,
  owner: 'kb',
})

export const KbSearchInputSchema = z.object({
  query: z.string().min(1).max(500),
  limit: z.number().int().min(1).max(KB_SEARCH_MAX_LIMIT).optional(),
})

export const KbSearchTool = defineRunTool({
  name: 'kb_search',
  description: 'Full-text search the knowledge base; returns path, title and a matching fragment per page.',
  input: KbSearchInputSchema,
  availability: { setting: 'kb' },
  subtask: true,
  waits: false,
  owner: 'kb',
})

export const KbForPathsInputSchema = z.object({
  /** Repository-relative file paths. */
  paths: z.array(z.string().min(1).max(500)).min(1).max(KB_FOR_PATHS_MAX_FILES),
})

export const KbForPathsTool = defineRunTool({
  name: 'kb_for_paths',
  description: 'Find knowledge base pages whose applies_to globs cover the given repository files (up to 50); call before changing them.',
  input: KbForPathsInputSchema,
  availability: { setting: 'kb' },
  subtask: true,
  waits: false,
  owner: 'kb',
})

export const KbWriteInputSchema = z.object({
  path: z.string().min(1).max(220),
  /** Full page text, YAML frontmatter included. */
  content: z.string().min(1),
  /** Why — like a commit message. */
  message: z.string().min(1).max(500),
  /** The rev you read; a newer rev returns a conflict with the current text. */
  base_rev: z.number().int().min(0).optional(),
})

export const KbWriteTool = defineRunTool({
  name: 'kb_write',
  description: 'Create or update a knowledge base page (max 20 per run); returns applied, proposed (needs review) or a conflict.',
  input: KbWriteInputSchema,
  availability: { setting: 'kb' },
  subtask: false,
  waits: false,
  owner: 'kb',
})

/** Knowledge base tools that need `runSettings.kb.write` on top of `kb.enabled`. */
export const KB_WRITE_TOOLS: readonly string[] = ['kb_write']

// ---------------------------------------------------------------------------
// Reports — gated by runSettings.reports (docs/plans/2026-10-02-rapporten-
// ontwerp.md §6.1). One report per task; API side: /internal/runs/:runId/report*.
// ---------------------------------------------------------------------------

/** Report tools that need `runSettings.reports.write`; every other report tool needs `.read`. */
export const REPORT_WRITE_TOOLS: readonly string[] = ['report_write', 'report_attach']

export const ReportWriteInputSchema = z.object({
  title: z.string().min(1).max(REPORT_TITLE_MAX_LENGTH),
  /** HTML fragment or full document, ≤ 1 MB; embed attachments as `assets/<name>`. */
  html: z.string().min(1).max(REPORT_MAX_HTML_BYTES),
  summary: z.string().max(REPORT_SUMMARY_MAX_LENGTH).optional(),
  /** What changed in this version — like a commit message. */
  message: z.string().min(1).max(REPORT_MESSAGE_MAX_LENGTH),
})

export const ReportWriteTool = defineRunTool({
  name: 'report_write',
  description: "Create or update the task's HTML report for humans (a new version each call, max 20 per run); embed attachments as assets/<name>.",
  input: ReportWriteInputSchema,
  availability: { setting: 'reports' },
  subtask: false,
  waits: false,
  owner: 'reports',
})

export const ReportAttachInputSchema = z.object({
  /** A file in the working directory or /tmp of the sandbox. */
  path: z.string().min(1).max(1000),
  /** Asset name (`[a-z0-9._-]`, with extension); default: derived from the file name. */
  name: z.string().min(1).max(100).optional(),
})

export const ReportAttachTool = defineRunTool({
  name: 'report_attach',
  description: 'Attach a sandbox file (PNG, JPEG, GIF, WebP, SVG, CSV, JSON or PDF, max 5 MB) to the task report; returns the assets/<name> to embed.',
  input: ReportAttachInputSchema,
  availability: { setting: 'reports' },
  subtask: false,
  waits: false,
  owner: 'reports',
})

export const ReportReadInputSchema = z.object({
  /** Omitted = the current version. */
  version: z.number().int().min(1).optional(),
})

export const ReportReadTool = defineRunTool({
  name: 'report_read',
  description: "Read the task's report (HTML, attachments) and the form submissions humans sent since the last run.",
  input: ReportReadInputSchema,
  availability: { setting: 'reports' },
  subtask: true,
  waits: false,
  owner: 'reports',
})

// ---------------------------------------------------------------------------
// The catalog
// ---------------------------------------------------------------------------

export const RUN_TOOLS: readonly RunToolDef[] = [
  GetTaskTool,
  ReportProgressTool,
  FinishTool,
  AskHumanTool,
  RequestApprovalTool,
  PostCommentTool,
  CreateFollowupTool,
  CreateSubtasksTool,
  WebSearchTool,
  WebFetchTool,
  DelegateTool,
  KbListTool,
  KbReadTool,
  KbSearchTool,
  KbForPathsTool,
  KbWriteTool,
  ReportWriteTool,
  ReportAttachTool,
  ReportReadTool,
]

/**
 * The 3 core tools every role gets, with no `runSettings` switch able to
 * turn them off (04 §2.4's token budget applies to these, plus whatever
 * `isRunToolAllowedBySettings` grants on top). `get_task`/`report_progress`/
 * `finish` only — `ask_human`/`request_approval`/`post_comment`/
 * `create_followup` moved to gated (05's `runSettings.tools.*`, each still
 * on by default, just no longer unconditional).
 */
export const DEFAULT_RUN_TOOLS: readonly RunToolDef[] = RUN_TOOLS.filter((tool) => tool.availability === 'default')

/** The `runSettings` fields `isRunToolAllowedBySettings` reads (a structural subset of `RoleRunSettings`). */
export interface RunToolSettingsView {
  readonly web: { readonly enabled: boolean }
  readonly delegate: { readonly enabled: boolean }
  readonly kb: { readonly enabled: boolean; readonly write: boolean }
  readonly tools: {
    readonly askHuman: boolean
    readonly requestApproval: boolean
    readonly postComment: boolean
    readonly createFollowup: boolean
    readonly createSubtasks: boolean
  }
  readonly reports: { readonly read: boolean; readonly write: boolean }
}

/**
 * Whether a role's run settings switch `tool` on. A `'default'`-availability
 * tool always is — there's no switch to check. Note the API still drops
 * `web_search` when no search connection resolves (05 §2.2) — that is a
 * dispatch-time check, not a setting.
 */
export function isRunToolAllowedBySettings(tool: RunToolDef, settings: RunToolSettingsView): boolean {
  if (tool.availability === 'default') return true
  switch (tool.availability.setting) {
    case 'web':
      return settings.web.enabled
    case 'delegate':
      return settings.delegate.enabled
    case 'askHuman':
      return settings.tools.askHuman
    case 'requestApproval':
      return settings.tools.requestApproval
    case 'postComment':
      return settings.tools.postComment
    case 'createFollowup':
      return settings.tools.createFollowup
    case 'createSubtasks':
      return settings.tools.createSubtasks
    case 'kb':
      return settings.kb.enabled && (!KB_WRITE_TOOLS.includes(tool.name) || settings.kb.write)
    case 'reports':
      return REPORT_WRITE_TOOLS.includes(tool.name) ? settings.reports.write : settings.reports.read
  }
}

export function getRunTool(name: string): RunToolDef | undefined {
  return RUN_TOOLS.find((tool) => tool.name === name)
}

/**
 * Converts one tool's Zod input to a plain JSON Schema object, for
 * `/tmp/.goblic-run/tools.json` (04) and the `goblic-tools` MCP server's
 * tool registration. Zod's own `.toJSONSchema()` is a v4-only API; the
 * `zod`package in this repo ships v3 semantics by default (see the package's
 * `./v4` subpath), so conversion goes through `zod-to-json-schema` instead,
 * which supports the v3 schema instances used throughout this codebase.
 */
export function runToolInputJsonSchema(tool: RunToolDef): Record<string, unknown> {
  return zodToJsonSchema(tool.input, { target: 'jsonSchema7', $refStrategy: 'none' }) as Record<string, unknown>
}
